/**
 * Decides what a row drag-and-drop landed on, from the bundle render plan alone.
 *
 * The single answer `ArTableBody.handleDragEnd` uses to pick between joining a
 * bundle, leaving one, moving a whole bundle, and a plain reorder. Membership is
 * read off the DROP TARGET, never off the dragged row: a drop onto a group header
 * or a foreign member joins that bundle even when the dragged row carries no
 * bundle fields (SYD-2323). A dragged header always moves its whole bundle and
 * can never be demoted into a member, which would orphan the rows it owns.
 *
 * A bundle is a contiguous block, so it can never be dropped INTO another one.
 * `moveBundle` therefore reports `intoBundleId` — again read off the drop
 * target — and `handleDragEnd` clamps the insertion to that bundle's nearest
 * rendered boundary. Without it a header ranked between its two rendered
 * neighbours lands mid-block and draws the target bundle split in half.
 */

import type { ArRowId } from '#types';
import type { BundleRenderItem } from './bundleDisplay';

type ArRowDropTarget =
  | { kind: 'joinBundle'; bundleId: string }
  | { kind: 'leaveBundle' }
  /** `intoBundleId` is the drop target's bundle when it is a DIFFERENT one from `bundleId`, else null. */
  | { kind: 'moveBundle'; bundleId: string; intoBundleId: string | null }
  | { kind: 'reorder' };

type ArRowBundlePosition =
  | { kind: 'header'; bundleId: string }
  | { kind: 'member'; bundleId: string }
  | { kind: 'ungrouped' };

/**
 * All `bundlePositionOf` reads: an item's kind, its rowId, and a member's
 * bundleId. Narrower than `BundleRenderItem` on purpose, so the classification
 * `classifyArBundleRows` returns satisfies it too. `pasteRows` has no render
 * plan in scope, and widening this beats rebuilding one there with display
 * fields the position rule never consults.
 */
type ArBundleRowPositionItem =
  | { kind: 'bundleHeader'; rowId: ArRowId }
  | { kind: 'bundleMember'; rowId: ArRowId; bundleId: string }
  | { kind: 'ungrouped'; rowId: ArRowId }
  | { kind: 'bundleContinuation' };

/**
 * A header owns the bundle named by its own rowId, matching the persisted shape
 * (`bundleRole='header'`, `bundleId=own docId`). A rowId absent from the plan is
 * ungrouped and so can only reorder; `handleDragEnd` already returns before that,
 * since it drops any pair not both present in the rendered list. A
 * `bundleContinuation` stands for no document and carries no `rowId`, so it is
 * skipped — an orphan member still reports `{ kind: 'member' }` and a join onto
 * it still resolves (SYD-2430).
 */
function bundlePositionOf(items: readonly ArBundleRowPositionItem[], rowId: ArRowId): ArRowBundlePosition {
  const item = items.find((candidate) => (
    candidate.kind !== 'bundleContinuation' && candidate.rowId === rowId
  ));
  if (item?.kind === 'bundleHeader') return { kind: 'header', bundleId: rowId };
  if (item?.kind === 'bundleMember') return { kind: 'member', bundleId: item.bundleId };
  return { kind: 'ungrouped' };
}

function resolveArRowDrop(params: {
  items: readonly BundleRenderItem[];
  activeRowId: ArRowId;
  overRowId: ArRowId;
}): ArRowDropTarget {
  const active = bundlePositionOf(params.items, params.activeRowId);
  const over = bundlePositionOf(params.items, params.overRowId);

  if (active.kind === 'header') {
    const intoBundleId = over.kind !== 'ungrouped' && over.bundleId !== active.bundleId
      ? over.bundleId
      : null;
    return { kind: 'moveBundle', bundleId: active.bundleId, intoBundleId };
  }
  if (over.kind === 'ungrouped') {
    return active.kind === 'member' ? { kind: 'leaveBundle' } : { kind: 'reorder' };
  }
  if (active.kind === 'member' && active.bundleId === over.bundleId) return { kind: 'reorder' };
  return { kind: 'joinBundle', bundleId: over.bundleId };
}

export { bundlePositionOf, resolveArRowDrop };
export type { ArBundleRowPositionItem, ArRowBundlePosition };
